import { useEffect, useMemo, useRef, useState } from 'react';
import type { PileTypeId, StructuralPieceId } from '@obraplan/core/domain/shared/ids';
import { checkPileFields, isPositioned, type PileType } from '@obraplan/core/domain/takeoff/pile';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import {
  contagemDasMarcas,
  filaDeEstacas,
  proximoNomeDeEstaca,
  seguinteNaFila,
  servicosMarcaveis,
} from './estacas-da-prancha';
import { distance, pixelToMetres, type Pixel } from './plan-tracer';
import type { TypologyDraft, WorkspaceAction } from './typology-draft';

/**
 * O que a ferramenta "Estaca" faz na prancha (Fase 27, etapa 1b — D1, D4, D5, D16).
 *
 * São três jeitos de mexer na estaca, e um só vale por vez:
 *
 * - **Nova**: o clique marca o centro, o painel diz o tipo e o nome, e "Adicionar estaca"
 *   cadastra. O nome seguinte já vem sugerido;
 * - **Posicionar**: a fila do "faltam N" — as estacas importadas chegam sem lugar, e cada
 *   clique põe a próxima no centro dela (D5);
 * - **Marcar**: o clique alterna a marca da estaca para o serviço escolhido (D4).
 *
 * O estado mora aqui, e não dentro do traçado, porque o traçado já carrega o de seis
 * ferramentas: o hook devolve o que o painel mostra e as funções que os eventos do desenho
 * chamam.
 */

export type AcaoDaEstaca = 'NOVA' | 'POSICIONAR' | 'MARCAR';

/** A estaca na mão, arrastada: o pixel por onde ela passa até o botão subir. */
export interface EstacaArrastada {
  readonly id: StructuralPieceId;
  /** Onde o botão desceu. */
  readonly origem: Pixel;
  readonly atual: Pixel;
  /** Passou da folga do clique? Antes disso é clique, e clicar seleciona. */
  readonly moveu: boolean;
}

/** A folga do clique, em pixels **de tela**, como a do encaixe do contorno. */
const FOLGA_DO_ARRASTO = 4;

/** Quanto tempo depois de soltar um arrasto o clique que veio com ele ainda é dele. */
const JANELA_DO_CLIQUE_MS = 100;

export function useEstacasNaPrancha({
  draft,
  pileTypes,
  catalog,
  escala,
  zoom,
  ativo,
  dispatch,
  selecionar,
}: {
  draft: TypologyDraft;
  pileTypes: readonly PileType[];
  catalog: ServiceCatalog | undefined;
  escala: number | undefined;
  zoom: number;
  /** A ferramenta está ligada? Parada, a prancha só seleciona e arrasta a estaca. */
  ativo: boolean;
  dispatch: React.Dispatch<WorkspaceAction>;
  /** Selecionar uma estaca é da prancha: é ela que sabe que o vão e o pilar também selecionam. */
  selecionar: (id: StructuralPieceId | undefined) => void;
}) {
  const pecas = useMemo(
    () => (draft.pieces ?? []).filter((peca) => peca.deletedAt === undefined),
    [draft.pieces],
  );
  const marcas = draft.pieceMarks ?? [];

  const [acaoEscolhida, setAcaoEscolhida] = useState<AcaoDaEstaca | undefined>(undefined);
  const [tipoEscolhido, setTipoEscolhido] = useState<PileTypeId | undefined>(undefined);
  const [nomeDigitado, setNomeDigitado] = useState<string | undefined>(undefined);
  const [centro, setCentro] = useState<Pixel | undefined>(undefined);
  const [daFila, setDaFila] = useState<StructuralPieceId | undefined>(undefined);
  const [servicoEscolhido, setServicoEscolhido] = useState<string | undefined>(undefined);
  const [filtroDoTipo, setFiltroDoTipo] = useState<PileTypeId | undefined>(undefined);
  const [arrastando, setArrastando] = useState<EstacaArrastada | undefined>(undefined);
  /** Quando o último arrasto terminou — o clique que vem junto com ele não é de ninguém. */
  const cliqueDoArrasto = useRef<number | undefined>(undefined);

  const { fila, porTipo } = useMemo(() => filaDeEstacas(pecas, pileTypes), [pecas, pileTypes]);

  // Com estaca na fila a ferramenta abre posicionando; sem ela, cadastrando. A escolha de
  // quem clicou numa aba vale — menos "Posicionar" quando a fila acabou.
  const acao: AcaoDaEstaca =
    acaoEscolhida === 'POSICIONAR' && fila.length === 0
      ? 'NOVA'
      : (acaoEscolhida ?? (fila.length > 0 ? 'POSICIONAR' : 'NOVA'));

  const tipoDaNova = pileTypes.find((tipo) => tipo.id === tipoEscolhido)?.id ?? pileTypes[0]?.id;
  const nome = nomeDigitado ?? proximoNomeDeEstaca(pecas);
  const proxima = fila.find((peca) => peca.id === daFila) ?? fila[0];

  const servicos = useMemo(() => servicosMarcaveis(catalog), [catalog]);
  const servico = servicos.find((candidato) => candidato.key === servicoEscolhido) ?? servicos[0];
  const filtro = pileTypes.find((tipo) => tipo.id === filtroDoTipo)?.id;
  const contagem =
    servico === undefined ? undefined : contagemDasMarcas(pecas, marcas, servico.key);

  // Trocar de tipologia descarta o que estava na mão: o centro marcado é da outra.
  const emDesenho = draft.unitTypeId;
  useEffect(() => {
    setCentro(undefined);
    setArrastando(undefined);
    setDaFila(undefined);
    setNomeDigitado(undefined);
  }, [emDesenho]);

  // Desligada a ferramenta, o centro marcado e não cadastrado não fica pendurado.
  useEffect(() => {
    if (!ativo) setCentro(undefined);
  }, [ativo]);

  /** O que impede cadastrar a estaca de agora — o painel diz, e o botão espera. */
  const impedimento = useMemo((): string | undefined => {
    if (pileTypes.length === 0) {
      return 'Cadastre o tipo de estaca antes: é dele que sai o diâmetro.';
    }
    const conferida = checkPileFields(
      tipoDaNova === undefined ? { name: nome } : { name: nome, pileTypeId: tipoDaNova },
      pecas,
    );
    return conferida.ok ? undefined : conferida.error.message;
  }, [pileTypes.length, nome, tipoDaNova, pecas]);

  function centroEmMetros(pixel: Pixel) {
    return pixelToMetres(pixel, escala ?? 1);
  }

  /** O clique no vazio da prancha, com a ferramenta ligada. */
  function clicarNaPrancha(bruto: Pixel): void {
    if (escala === undefined) return;
    if (acao === 'NOVA') {
      setCentro(bruto);
      return;
    }
    if (acao === 'POSICIONAR' && proxima !== undefined) {
      dispatch({ type: 'PLACE_PILE', pieceId: proxima.id, center: centroEmMetros(bruto) });
      // A seguinte entra sozinha — a que vem depois desta na fila, e não a primeira dela:
      // quem pulou uma estaca não volta a ela a cada clique.
      setDaFila(seguinteNaFila(fila, proxima.id));
    }
  }

  function adicionar(): void {
    if (centro === undefined || impedimento !== undefined || tipoDaNova === undefined) return;
    dispatch({
      type: 'ADD_PILE',
      name: nome,
      pileTypeId: tipoDaNova,
      center: centroEmMetros(centro),
    });
    setCentro(undefined);
    setNomeDigitado(undefined);
  }

  /** O botão desceu em cima de uma estaca: se andar, ela vai junto. */
  function apertar(id: StructuralPieceId, ponto: Pixel): void {
    setArrastando({ id, origem: ponto, atual: ponto, moveu: false });
  }

  function mover(ponto: Pixel): void {
    setArrastando((corrente) =>
      corrente === undefined
        ? corrente
        : {
            ...corrente,
            atual: ponto,
            moveu: corrente.moveu || distance(corrente.origem, ponto) > FOLGA_DO_ARRASTO / zoom,
          },
    );
  }

  function soltar(ponto: Pixel): void {
    const corrente = arrastando;
    setArrastando(undefined);
    if (corrente === undefined || escala === undefined) return;
    // Soltar onde apertou é clique, e clicar seleciona: a estaca não sai do lugar.
    if (!corrente.moveu && distance(corrente.origem, ponto) <= FOLGA_DO_ARRASTO / zoom) return;
    cliqueDoArrasto.current = Date.now();
    dispatch({ type: 'MOVE_PILE', pieceId: corrente.id, center: centroEmMetros(ponto) });
  }

  /**
   * O clique que o fim do arrasto dispara é do arrasto; consome-o, e só uma vez.
   *
   * O navegador o entrega no mesmo instante em que o botão sobe; a janela existe para que um
   * arrasto cujo clique nunca veio — soltar fora da prancha — não engula o clique seguinte,
   * que é de outra pessoa.
   */
  function foiCliqueDoArrasto(): boolean {
    const desde = cliqueDoArrasto.current;
    cliqueDoArrasto.current = undefined;
    return desde !== undefined && Date.now() - desde < JANELA_DO_CLIQUE_MS;
  }

  /** O clique numa estaca: marca, no modo Marcar; senão, seleciona. */
  function clicarNaEstaca(id: StructuralPieceId): void {
    if (foiCliqueDoArrasto()) return;
    if (ativo && acao === 'MARCAR') {
      if (servico !== undefined) {
        dispatch({ type: 'TOGGLE_PIECE_MARK', pieceId: id, serviceKey: servico.key });
      }
      return;
    }
    selecionar(id);
  }

  return {
    acao,
    escolherAcao: setAcaoEscolhida,
    tipos: pileTypes,
    tipoDaNova,
    escolherTipo: setTipoEscolhido,
    nome,
    digitarNome: setNomeDigitado,
    centro,
    impedimento,
    adicionar,
    fila,
    porTipo,
    proxima,
    escolherDaFila: setDaFila,
    pular: () => {
      if (proxima !== undefined) setDaFila(seguinteNaFila(fila, proxima.id));
    },
    servicos,
    servico,
    escolherServico: setServicoEscolhido,
    filtro,
    escolherFiltro: setFiltroDoTipo,
    contagem,
    marcarTodas: () => {
      if (servico === undefined) return;
      dispatch({
        type: 'MARK_ALL_PIECES',
        serviceKey: servico.key,
        ...(filtro === undefined ? {} : { pileTypeId: filtro }),
      });
    },
    desmarcarTodas: () => {
      if (servico === undefined) return;
      dispatch({ type: 'UNMARK_ALL_PIECES', serviceKey: servico.key });
    },
    arrastando,
    apertar,
    mover,
    soltar,
    cancelarArrasto: () => setArrastando(undefined),
    foiCliqueDoArrasto,
    clicarNaPrancha,
    clicarNaEstaca,
    pecas,
    posicionadas: pecas.filter(isPositioned).length,
  };
}

export type EstacasNaPrancha = ReturnType<typeof useEstacasNaPrancha>;
